import { useState, useCallback } from 'react';
import {
    Box, Typography, Chip, Collapse, Divider, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getRuleById } from '../../../gibbs/knowledge/medchemRules';

const SEVERITY_COLOR = {
    severe: 'error',
    warning: 'warning',
    info: 'default',
};

const SEVERITY_GLYPH = {
    severe: '🔴',
    warning: '🟡',
    info: 'ℹ',
};

function formatNumber(v) {
    if (v === null || v === undefined || !Number.isFinite(v)) return '—';
    return v.toFixed(2);
}

function FlagDetail({ flag }) {
    const rule = getRuleById(flag.id);
    const ruleText = rule ? (rule.alert || rule.principle || rule.id) : flag.id;
    const mechanism = rule && rule.mechanism;
    const mitigation = rule && rule.mitigation;
    const citation = (rule && rule.references && rule.references[0])
        || flag.citation
        || 'no citation';

    return (
        <Box
            sx={{
                pl: 1, py: 0.5, borderLeft: '2px solid', borderColor: 'divider',
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {ruleText}
            </Typography>
            {mechanism && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {`Mechanism: ${mechanism}`}
                </Typography>
            )}
            {mitigation && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {`Mitigation: ${mitigation}`}
                </Typography>
            )}
            <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                {citation}
            </Typography>
        </Box>
    );
}

export default function CompoundDrilldown({
    compound,
    rationale,
    groundedRationale,
    isGroundedLoading,
    onRequestGrounded,
}) {
    const [expandedFlagId, setExpandedFlagId] = useState(null);

    const toggleFlag = useCallback((flagId) => {
        setExpandedFlagId((curr) => (curr === flagId ? null : flagId));
    }, []);

    if (!compound || !rationale) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                Select a compound to see its decision rationale.
            </Typography>
        );
    }

    // Prefer the grounded rationale (success path) but never replace the
    // prose with anything that didn't pass the schema + anti-hallucination
    // guards. On fallback, the grounded result still has the four numeric
    // fields + the template prose, just with `groundedFallback: true`.
    const groundedSucceeded = groundedRationale && !groundedRationale.groundedFallback;
    const showsFallbackNote = groundedRationale && groundedRationale.groundedFallback;
    const displayed = groundedSucceeded ? groundedRationale : rationale;

    const {
        rankExplanation, energyBreakdown, interactionSummary, flags, suggestedNext,
    } = displayed;

    return (
        <Box
            sx={{
                p: 1, display: 'flex', flexDirection: 'column', gap: 1,
            }}
        >
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="overline" color="text.secondary">
                        {`Compound ${compound.compoundId}`}
                    </Typography>
                    {!groundedSucceeded && onRequestGrounded && (
                        <Tooltip title="Explain in more detail (AI)">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onRequestGrounded(compound.compoundId)}
                                    disabled={isGroundedLoading}
                                    aria-label="explain in more detail"
                                >
                                    {isGroundedLoading ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <AutoAwesomeIcon fontSize="inherit" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
                <Typography variant="body2">{rankExplanation}</Typography>
                {showsFallbackNote && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', display: 'block', mt: 0.25 }}
                    >
                        AI explanation unavailable — showing template.
                    </Typography>
                )}
            </Box>

            {energyBreakdown && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Energies</Typography>
                    {Object.entries(energyBreakdown).map(([k, v]) => (
                        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption">{k}</Typography>
                            <Typography variant="caption">{formatNumber(v)}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {interactionSummary && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Interactions</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">H-bonds</Typography>
                        <Typography variant="caption">{interactionSummary.hydrogenBonds}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">Hydrophobic</Typography>
                        <Typography variant="caption">{interactionSummary.hydrophobicContacts}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">π-stacking</Typography>
                        <Typography variant="caption">{interactionSummary.piStacking}</Typography>
                    </Box>
                    {interactionSummary.keyResidues
                        && interactionSummary.keyResidues.length > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {`Key residues: ${interactionSummary.keyResidues.join(', ')}`}
                        </Typography>
                    )}
                </Box>
            )}

            {flags && flags.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                        {`Alerts (${flags.length})`}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5,
                        }}
                    >
                        {flags.map((f) => (
                            <Chip
                                key={f.id}
                                size="small"
                                color={SEVERITY_COLOR[f.severity] || 'default'}
                                label={`${SEVERITY_GLYPH[f.severity] || ''} ${f.id}`}
                                onClick={() => toggleFlag(f.id)}
                                variant={expandedFlagId === f.id ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Box>
                    {flags.map((f) => (
                        <Collapse key={`${f.id}-detail`} in={expandedFlagId === f.id}>
                            <Box sx={{ mt: 0.5 }}>
                                <FlagDetail flag={f} />
                            </Box>
                        </Collapse>
                    ))}
                </Box>
            )}

            {suggestedNext && (
                <>
                    <Divider />
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                            Suggested next
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {suggestedNext}
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );
}
